
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Search,
  Download,
  FileText,
  DollarSign,
  Loader2
} from "lucide-react";
import PledgeTracking from "./PledgeTracking";
import { db, auth } from "@/firebase/config";
import { collection, getDocs, getDoc, doc, query, where } from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";

interface StudentFee {
  id: string;
  studentName: string;
  registrationNumber: string;
  class: string;
  totalFees: number;
  paidAmount: number;
  balance: number;
  status: 'paid' | 'partial' | 'unpaid';
  studentType: string;
}

interface FeeStructureLevel {
  level: string;
  classes: string[];
  categories: Array<{
    name: string;
    term1: number;
    term2: number;
    term3: number;
    daySchoolTerm1?: number;
    daySchoolTerm2?: number;
    daySchoolTerm3?: number;
    paidOnce?: boolean;
  }>;
  isGovernmentSponsored?: boolean;
  governmentSponsorshipAmount?: number;
}

import PaymentSimulator from "./PaymentSimulator";
import { getApplicableTermFees, Term } from "@/utils/feeCalculations";

const StudentFeeManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("all");
  const [feeStatus, setFeeStatus] = useState("all");
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [studentFees, setStudentFees] = useState<StudentFee[]>([]);
  const [availableClasses, setAvailableClasses] = useState<string[]>([]);
  const [schoolType, setSchoolType] = useState<string>("");

  // Simulator State
  const [simState, setSimState] = useState<{
    isOpen: boolean;
    studentId: string;
    studentName: string;
  }>({
    isOpen: false,
    studentId: "",
    studentName: ""
  });

  // Get schoolID from auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { setSchoolID(null); return; }
      try {
        const token = await getIdTokenResult(u, true);
        const affiliations = (token.claims.affiliations as Array<{ role: string; schoolID?: string }> | undefined) || [];
        const defaultAff = token.claims.defaultAffiliation as { role?: string; schoolID?: string } | undefined;
        const pick =
          (affiliations.find(a => a.role === 'admin' && a.schoolID?.trim()) ||
           affiliations.find(a => a.role === 'school_staff' && a.schoolID?.trim()) ||
           defaultAff);
        const sid = pick?.schoolID?.trim() || null;
        setSchoolID(sid);
      } catch (e) {
        console.error('Failed to get schoolID:', e);
        setSchoolID(null);
      }
    });
    return () => unsub();
  }, []);

  // Fetch student fees and calculate balances
  useEffect(() => {
    if (!schoolID) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Get school type
        const schoolRef = doc(db, `schools/${schoolID}/schoolDetails/main`);
        const schoolSnap = await getDoc(schoolRef);
        if (schoolSnap.exists()) {
          const fetchedSchoolType = schoolSnap.data().type || "";
          setSchoolType(fetchedSchoolType);
          console.log('[StudentFeeManagement] School type:', fetchedSchoolType);
        }
        
        // Get deployed fee structure
        const feeStructureRef = doc(db, `schools/${schoolID}/deployedFeeStructures/current`);
        const feeStructureSnap = await getDoc(feeStructureRef);
        
        if (!feeStructureSnap.exists()) {
          console.log('No deployed fee structure found');
          setLoading(false);
          return;
        }
        
        const feeStructure = feeStructureSnap.data();
        const levelFees = (feeStructure.levelFees || []) as FeeStructureLevel[];
        
        // Get all students
        const studentsQuery = query(collection(db, `schools/${schoolID}/students`));
        const studentsSnap = await getDocs(studentsQuery);
        
        // Fetch term dates for eligibility checking
        // Priority: 1) School-specific terms, 2) Global system config
        let termRanges: { term1: { start: Date; end: Date }, term2: { start: Date; end: Date }, term3: { start: Date; end: Date } } | null = null;
        
        // First try school-specific terms
        try {
          const termsSnap = await getDocs(collection(db, `schools/${schoolID}/terms`));
          if (!termsSnap.empty) {
            const termsData: Record<string, { start: Date; end: Date }> = {};
            
            termsSnap.docs.forEach((termDoc) => {
              const data = termDoc.data();
              const termNumber = data.number;
              
              // Parse dates - stored as yyyy-MM-dd strings
              const parseDate = (dateStr: string) => {
                if (!dateStr) return null;
                const d = new Date(dateStr);
                return isNaN(d.getTime()) ? null : d;
              };
              
              const openingDate = parseDate(data.openingDate);
              const closingDate = parseDate(data.closingDate);
              
              if (termNumber && openingDate && closingDate) {
                termsData[`term${termNumber}`] = { start: openingDate, end: closingDate };
              }
            });
            
            if (termsData.term1 && termsData.term2 && termsData.term3) {
              termRanges = {
                term1: termsData.term1,
                term2: termsData.term2,
                term3: termsData.term3,
              };
              console.log('[StudentFeeManagement] Loaded school-specific term dates:', termsData);
            }
          }
        } catch (e) {
          console.warn('[StudentFeeManagement] Failed to fetch school terms:', e);
        }
        
        // Fallback to global config if school terms not found
        if (!termRanges) {
          try {
            const globalCfg = await getDoc(doc(db, 'systemConfig/global'));
            if (globalCfg.exists()) {
              const td = globalCfg.data()?.termDates;
              if (td) {
                const currentYear = new Date().getFullYear();
                const parseMMDD = (mmdd: string, yr: number) => {
                  const [m, d] = mmdd.split('-').map(Number);
                  return new Date(yr, (m || 1) - 1, d || 1);
                };
                termRanges = {
                  term1: { start: parseMMDD(td.term1Start, currentYear), end: parseMMDD(td.term1End, currentYear) },
                  term2: { start: parseMMDD(td.term2Start, currentYear), end: parseMMDD(td.term2End, currentYear) },
                  term3: { start: parseMMDD(td.term3Start, currentYear), end: parseMMDD(td.term3End, currentYear) },
                };
                console.log('[StudentFeeManagement] Loaded global term dates (fallback):', td);
              }
            }
          } catch (e) {
            console.warn('[StudentFeeManagement] Failed to fetch global term dates:', e);
          }
        }
        
        const studentFeesData: StudentFee[] = [];
        const classSet = new Set<string>();

        // Optimized: Pre-fetch all completed transactions once to avoid N+1 queries.
        // This ensures identical logic to the per-student query but much faster.
        const providers = ['mpesa', 'equity', 'kcb', 'cash', 'card', 'bank'];
        const transactionsMap: Record<string, number> = {};
        await Promise.all(providers.map(async (provider) => {
          try {
            const q = query(
              collection(db, `schools/${schoolID}/transactions/${provider}/records`),
              where('status', '==', 'completed')
            );
            const snap = await getDocs(q);
            snap.forEach(d => {
              const data = d.data();
              const ref = data.accountReference;
              if (ref) {
                const amt = Number(data.amount || 0);
                const type = String(data.type || 'credit').toLowerCase();
                if (!transactionsMap[ref]) transactionsMap[ref] = 0;
                transactionsMap[ref] += (type === 'debit' ? -amt : amt);
              }
            });
          } catch (e) {
            console.error(`Error pre-fetching ${provider} transactions:`, e);
          }
        }));
        
        for (const studentDoc of studentsSnap.docs) {
          const studentData = studentDoc.data();
          const studentClass = studentData.className || studentData.class || "";
          const studentGrade = studentData.admissionGrade || studentData.grade || "";
          const studentType = studentData.studentType || "";
          const registrationNumber = studentData.registrationNumber || "";
          const firstName = studentData.firstName || "";
          const lastName = studentData.lastName || "";
          
          if (studentClass) classSet.add(studentClass);
          
          // Determine term eligibility using ONLY admissionDate - no assumptions
          let eligibleForTerm1 = true;
          let eligibleForTerm2 = true;
          let eligibleForTerm3 = true;
          
          // ONLY use the admissionDate field - no fallbacks
          let admissionDate: Date | null = null;
          
          if (studentData.admissionDate) {
            if (typeof studentData.admissionDate === 'string') {
              admissionDate = new Date(studentData.admissionDate);
            } else if (typeof studentData.admissionDate?.toDate === 'function') {
              try { admissionDate = studentData.admissionDate.toDate(); } catch {}
            }
          }
          
          if (admissionDate && isNaN(admissionDate.getTime())) {
            admissionDate = null;
          }
          
          // Only calculate eligibility if admissionDate exists
          if (admissionDate && termRanges) {
            eligibleForTerm1 = admissionDate <= termRanges.term1.end;
            eligibleForTerm2 = admissionDate <= termRanges.term2.end;
            eligibleForTerm3 = admissionDate <= termRanges.term3.end;
          } else if (!admissionDate) {
            // Mark student as having missing admission date
            console.warn(`[StudentFeeManagement] No admission date for ${firstName} ${lastName} (${registrationNumber})`);
          }
          
          // Find matching fee structure
          let matchingLevel = null;
          
          if (studentClass) {
            matchingLevel = levelFees.find((level: any) => 
              level.classes && level.classes.some((c: string) => 
                c.toLowerCase() === studentClass.toLowerCase() ||
                studentClass.toLowerCase().includes(c.toLowerCase())
              )
            );
          }
          
          if (!matchingLevel && studentGrade) {
            matchingLevel = levelFees.find((level: any) => 
              level.level && level.level.toLowerCase() === studentGrade.toLowerCase()
            );
          }
          
          let totalFees = 0;
          
          if (matchingLevel) {
            const categories = matchingLevel.categories || [];
            const isDaySchool = studentType === "Day School";
            
            let grossT1 = 0;
            let grossT2 = 0;
            let grossT3 = 0;
            
            categories.forEach((category: any) => {
              const v1 = isDaySchool ? (category.daySchoolTerm1 ?? category.term1 ?? 0) : (category.term1 ?? 0);
              const v2 = isDaySchool ? (category.daySchoolTerm2 ?? category.term2 ?? 0) : (category.term2 ?? 0);
              const v3 = isDaySchool ? (category.daySchoolTerm3 ?? category.term3 ?? 0) : (category.term3 ?? 0);
              
              if (category.paidOnce) {
                // Only charge in first eligible term
                if (eligibleForTerm1) {
                  grossT1 += Number(v1) || 0;
                } else if (eligibleForTerm2) {
                  grossT2 += Number(v1) || 0;
                } else if (eligibleForTerm3) {
                  grossT3 += Number(v1) || 0;
                }
              } else {
                // Regular fees - only for eligible terms
                if (eligibleForTerm1) grossT1 += Number(v1) || 0;
                if (eligibleForTerm2) grossT2 += Number(v2) || 0;
                if (eligibleForTerm3) grossT3 += Number(v3) || 0;
              }
            });
            
            let netT1 = grossT1;
            let netT2 = grossT2;
            let netT3 = grossT3;
            
            const isGov = matchingLevel.isGovernmentSponsored;
            const govAmount = Number(matchingLevel.governmentSponsorshipAmount || 0) || 0;
            
            if (isGov && govAmount > 0) {
              let remaining = govAmount;
              
              // Apply to eligible terms only
              if (eligibleForTerm1 && remaining > 0) {
                const d1 = Math.min(grossT1, remaining);
                netT1 = grossT1 - d1;
                remaining -= d1;
              }
              if (eligibleForTerm2 && remaining > 0) {
                const d2 = Math.min(grossT2, remaining);
                netT2 = grossT2 - d2;
                remaining -= d2;
              }
              if (eligibleForTerm3 && remaining > 0) {
                const d3 = Math.min(grossT3, remaining);
                netT3 = grossT3 - d3;
              }
            }
            
            totalFees = netT1 + netT2 + netT3;
          }
          
          // Use pre-fetched transactions map for performance
          const paidAmount = transactionsMap[`Fee-${registrationNumber}`] || 0;
          
          const balance = Math.max(0, totalFees - paidAmount);
          let status: 'paid' | 'partial' | 'unpaid' = 'unpaid';
          
          if (balance === 0) {
            status = 'paid';
          } else if (paidAmount > 0 && balance > 0) {
            status = 'partial';
          }
          
          studentFeesData.push({
            id: studentDoc.id,
            studentName: `${firstName} ${lastName}`.trim() || 'Unknown',
            registrationNumber,
            class: studentClass || 'Unassigned',
            totalFees,
            paidAmount,
            balance,
            status,
            studentType: studentType || 'Boarding'
          });
        }
        
        setStudentFees(studentFeesData);
        setAvailableClasses(Array.from(classSet).sort());
      } catch (error) {
        console.error('Error fetching student fees:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [schoolID]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">Paid</Badge>;
      case 'partial':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">Partial</Badge>;
      case 'unpaid':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">Unpaid</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const filteredStudents = studentFees.filter(student => {
    const matchesSearch = student.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.class.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClass = selectedClass === "all" || student.class.includes(selectedClass);
    const matchesStatus = feeStatus === "all" || student.status === feeStatus;
    
    return matchesSearch && matchesClass && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div>
          <h2 className="text-lg font-semibold">Student Fee Management</h2>
          <p className="text-sm text-muted-foreground">Manage fee structures and track student payments</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm">
            <FileText className="h-4 w-4 mr-2" />
            Generate Invoices
          </Button>
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="students" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="students">Student Fees</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="pledges">Pledge Tracking</TabsTrigger>
        </TabsList>

        <TabsContent value="students" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search students..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={selectedClass} onValueChange={setSelectedClass}>
                  <SelectTrigger className="w-full sm:w-[180px] h-10 transition-all border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 font-semibold focus:ring-indigo-500 rounded-lg">
                    <SelectValue placeholder="All Classes" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 shadow-xl rounded-xl">
                    <SelectItem value="all" className="font-medium focus:bg-slate-100 dark:focus:bg-slate-800 text-slate-700 dark:text-slate-300">All Classes</SelectItem>
                    {availableClasses.map((cls) => (
                      <SelectItem key={cls} value={cls} className="font-medium focus:bg-slate-100 dark:focus:bg-slate-800 text-slate-700 dark:text-slate-300">{cls}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={feeStatus} onValueChange={setFeeStatus}>
                  <SelectTrigger className="w-full sm:w-[180px] h-10 transition-all border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 font-semibold focus:ring-indigo-500 rounded-lg">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 shadow-xl rounded-xl">
                    <SelectItem value="all" className="font-medium focus:bg-slate-100 dark:focus:bg-slate-800 text-slate-700 dark:text-slate-300">All Status</SelectItem>
                    <SelectItem value="paid" className="font-medium focus:bg-slate-100 dark:focus:bg-slate-800 text-emerald-600 dark:text-emerald-400">Paid</SelectItem>
                    <SelectItem value="partial" className="font-medium focus:bg-slate-100 dark:focus:bg-slate-800 text-amber-600 dark:text-amber-400">Partial</SelectItem>
                    <SelectItem value="unpaid" className="font-medium focus:bg-slate-100 dark:focus:bg-slate-800 text-rose-600 dark:text-rose-400">Unpaid</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Student Fees Table */}
          <Card>
            <CardHeader>
              <CardTitle>Student Fee Status</CardTitle>
              <CardDescription>Track individual student fee payments and balances</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-muted-foreground">Loading student fees...</span>
                  </div>
                ) : filteredStudents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No students found matching your filters.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Reg No</TableHead>
                        <TableHead>Class</TableHead>
                        {schoolType === "Mixed (Day & Boarding)" && <TableHead>Type</TableHead>}
                        <TableHead>Total Fees</TableHead>
                        <TableHead>Paid</TableHead>
                        <TableHead>Balance</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredStudents.map((student) => (
                        <TableRow key={student.id} className="border-b border-border">
                          <TableCell className="font-medium">{student.studentName}</TableCell>
                          <TableCell className="font-mono text-sm">{student.registrationNumber}</TableCell>
                          <TableCell>{student.class}</TableCell>
                          {schoolType === "Mixed (Day & Boarding)" && <TableCell>{student.studentType}</TableCell>}
                          <TableCell>KES {student.totalFees.toLocaleString()}</TableCell>
                          <TableCell>KES {student.paidAmount.toLocaleString()}</TableCell>
                          <TableCell className={student.balance > 0 ? "text-red-600 dark:text-red-400 font-medium" : "text-green-600 dark:text-green-400"}>
                            KES {student.balance.toLocaleString()}
                          </TableCell>
                          <TableCell>{getStatusBadge(student.status)}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" title="View Details">
                                <FileText className="h-3 w-3" />
                              </Button>
                              <Button variant="outline" size="sm" title="Record Payment">
                                <DollarSign className="h-3 w-3" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                title="Simulate Payment Logic"
                                className="text-blue-600 border-blue-200 dark:text-blue-400 dark:border-blue-900 bg-blue-50 dark:bg-blue-900/10 hover:bg-blue-100 dark:hover:bg-blue-900/20"
                                onClick={() => {
                                  setSimState({
                                    isOpen: true,
                                    studentId: student.id,
                                    studentName: student.studentName
                                  });
                                }}
                              >
                                <span className="text-xs font-bold">Simulate</span>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Invoice Management</CardTitle>
              <CardDescription>Generate and manage student invoices</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Invoice Management</h3>
                <p className="text-muted-foreground mb-4">Generate invoices for students and track payment status</p>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Generate New Invoices
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pledges" className="space-y-4">
          <PledgeTracking />
        </TabsContent>
      </Tabs>
      
      {schoolID && simState.isOpen && (
        <PaymentSimulator 
           isOpen={simState.isOpen}
           onClose={() => setSimState({ ...simState, isOpen: false })}
           studentId={simState.studentId}
           studentName={simState.studentName}
           schoolId={schoolID}
        />
      )}
    </div>
  );
};

export default StudentFeeManagement;